/* eslint-disable react-hooks/exhaustive-deps */
import { GridColDef } from "@mui/x-data-grid";
import { AppTable, DeleteConfirmDialog } from "components/molecules";
import { TableHelper } from "helpers";
import { usePaginationRequest } from "hooks/useRequest";
import {
  IDownloadExcelRequest,
  IPaginateApiResponse,
  IPaginationRequest,
  IWarehouse,
} from "model";
import { IZone } from "model/Zone/IZone";
import { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { DownloadActions, ZoneActions } from "redux/actions";
import { useAppSelector } from "redux/configurations/reduxHooks";
import { ZoneApiService } from "services";
import { CURRENT_PAGE_SIZE, EXPORT_FILE_NAME, FIRST_PAGE } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";

const ZoneConfiguration = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const action = useAppSelector((state) => state.ZoneReducer.action);
  const downloading = useAppSelector(
    (state) => state.AppConfigReducer.downloading
  );
  const [confirmDeleteZoneVisible, setConfirmDeleteZoneVisible] =
    useState(false);

  const [itemSelected, setItemSelected] = useState<IZone>();
  const [refresh, setRefresh] = useState(false);
  const [request, setRequest] = useState<IPaginationRequest>({
    pageNumber: FIRST_PAGE,
    pageSize: CURRENT_PAGE_SIZE,
    orderBy: "zoneCode",
  });

  const { loading, result } = usePaginationRequest<IPaginationRequest, IZone[]>(
    request,
    ZoneApiService.getAllZones,
    [],
    {
      refresh: refresh,
    },
    () => {
      setRefresh(false);
    }
  );

  const handleClickDeleteZone = (item: any) => {
    setItemSelected(item);
    setConfirmDeleteZoneVisible(true);
  };

  const handleConfirmDeleteClose = () => {
    setConfirmDeleteZoneVisible(false);
  };

  const handleAddNewClick = () => {
    history.push(`${location.pathname}/new`);
  };

  const deleteZoneSuccessfully = action.includes(
    ZoneActions.deleteZone.successName
  );
  const _deleteZone = (id: string) => {
    dispatch(
      ZoneActions.deleteZone.request({
        id: id,
      })
    );
  };
  const _clearAction = () => {
    dispatch(ZoneActions.clearAction.request());
  };

  const handleDeleteZone = () => {
    _deleteZone(itemSelected?.id!);
  };

  const handlePageChange = (pageNumber: number) => {
    setRequest({ ...request, pageNumber: pageNumber });
  };
  const handleSearchTextChange = (searchText: string) => {
    setRequest({ ...request, pageNumber: FIRST_PAGE, searchText: searchText });
  };
  const _handlePageSizeChange = (pageSize: number) => {
    setRequest({ ...request, pageSize: pageSize });
  };

  const _downloadZoneMasterData = () => {
    const params: IDownloadExcelRequest = {
      ...request,
      pageSize: (result as IPaginateApiResponse<IWarehouse[]>).totalRecords,
      pageNumber: FIRST_PAGE,
      fileName: EXPORT_FILE_NAME.zoneMasterData,
    };
    dispatch(DownloadActions.downloadZoneMasterData.request(params));
  };

  useEffect(() => {
    if (deleteZoneSuccessfully) {
      setRefresh(true);
      setConfirmDeleteZoneVisible(false);
      _clearAction();
    }
  }, [action]);

  const COLUMNS: GridColDef[] = [
    TableHelper.renderSTT(),
    TableHelper.createCell({
      field: "zoneCode",
      headerName: I18nTranslator(translations.masterData.zoneCode),
      size: "small",
      align: "left",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "zoneName",
      headerName: I18nTranslator(translations.masterData.zoneName),
      size: "medium",
      align: "left",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "description",
      headerName: I18nTranslator(translations.masterData.zoneDescription),
      size: "full",
      align: "left",
    }),
    TableHelper.renderActivationCell(),
  ];

  return (
    <Fragment>
      <AppTable<IZone>
        title={I18nTranslator(translations.masterData.zoneConfiguration)}
        loading={loading}
        data={result}
        columns={COLUMNS}
        onAddNewClick={handleAddNewClick}
        onSearch={handleSearchTextChange}
        searchPlaceholder={I18nTranslator(translations.placeholder.searchZone)}
        onChangePage={handlePageChange}
        onRowPressDelete={handleClickDeleteZone}
        onRowPressEdit={(row) =>
          history.push(`${location.pathname}/${row.id}&${row.zoneCode}`)
        }
        hideImport
        actionColumnProps={{ hideImport: true }}
        onPageSizeChange={_handlePageSizeChange}
        isDownloading={downloading}
        onDownloadClick={_downloadZoneMasterData}
      />
      <DeleteConfirmDialog
        open={confirmDeleteZoneVisible}
        value={itemSelected?.zoneName}
        handleClose={handleConfirmDeleteClose}
        handleDelete={handleDeleteZone}
        isDeleting={action.includes(ZoneActions.deleteZone.requestName)}
      />
    </Fragment>
  );
};

export default ZoneConfiguration;
